import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, ThumbsUp, Battery } from "lucide-react";

const MOODS = [
  {
    id: "ready",
    label: "Ready to Go",
    icon: Flame,
    color: "from-emerald-500 to-green-600",
    border: "border-emerald-500/50",
    bg: "bg-emerald-500/10",
    text: "text-emerald-400",
    desc: "Full send today",
  },
  {
    id: "alright",
    label: "Alright",
    icon: ThumbsUp,
    color: "from-blue-500 to-cyan-600",
    border: "border-blue-500/50",
    bg: "bg-blue-500/10",
    text: "text-blue-400",
    desc: "Feeling okay, let's work",
  },
  {
    id: "not_great",
    label: "Not Great",
    icon: Battery,
    color: "from-amber-500 to-orange-600",
    border: "border-amber-500/50",
    bg: "bg-amber-500/10",
    text: "text-amber-400",
    desc: "Low energy today",
  },
];

const ReadinessCheckIn = ({ onSelect, onSuggestDeload }) => {
  const [selected, setSelected] = useState(null);

  const handleSelect = (mood) => {
    setSelected(mood.id);
    if (mood.id === "not_great") {
      setTimeout(() => onSuggestDeload(mood.id), 600);
    } else {
      setTimeout(() => onSelect(mood.id), 600);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center min-h-[50vh] px-4"
      data-testid="readiness-checkin"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-center mb-8"
      >
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
          How are you feeling today?
        </h2>
        <p className="text-gray-400 text-sm">This helps tailor your workout</p>
      </motion.div>

      <div className="flex flex-col sm:flex-row gap-4 w-full max-w-lg">
        {MOODS.map((mood, i) => {
          const Icon = mood.icon;
          const isSelected = selected === mood.id;
          return (
            <motion.button
              key={mood.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.1 }}
              onClick={() => handleSelect(mood)}
              disabled={selected !== null}
              className={`flex-1 p-5 rounded-2xl border-2 transition-all ${
                isSelected
                  ? `${mood.border} ${mood.bg} scale-105`
                  : selected
                  ? "border-[#1E293B] opacity-30"
                  : "border-[#1E293B] hover:border-[#334155] hover:bg-[#141B2D]"
              }`}
              data-testid={`mood-${mood.id}`}
            >
              <div className={`w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br ${mood.color} flex items-center justify-center`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <p className={`font-semibold text-sm ${isSelected ? mood.text : "text-white"}`}>
                {mood.label}
              </p>
              <p className="text-xs text-gray-500 mt-1">{mood.desc}</p>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
};

const DeloadSuggestion = ({ onAcceptDeload, onContinueFull }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center min-h-[50vh] px-4"
      data-testid="deload-suggestion"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-md"
      >
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-amber-500/20 flex items-center justify-center">
          <Battery className="w-8 h-8 text-amber-400" />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
          Sounds like a lighter day
        </h2>
        <p className="text-gray-400 text-sm mb-8">
          We recommend switching to the deload version — half the reps, same movement quality. You can always push through the full workout if you change your mind.
        </p>
        <div className="flex flex-col gap-3">
          <button
            onClick={onAcceptDeload}
            className="w-full py-3 rounded-xl bg-amber-500 text-black font-semibold hover:bg-amber-400 transition-colors"
            data-testid="accept-deload-btn"
          >
            Switch to Deload
          </button>
          <button
            onClick={onContinueFull}
            className="w-full py-3 rounded-xl bg-[#1E293B] text-white font-semibold hover:bg-[#2D3B4F] transition-colors"
            data-testid="continue-full-btn"
          >
            I'll Push Through
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const MidWorkoutCheck = ({ blockName, setNum, onKeepPushing, onSwitchDeload }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bg-[#0F172A] border border-amber-500/30 rounded-xl p-4 my-3"
      data-testid="mid-workout-check"
    >
      <p className="text-sm font-semibold text-amber-400 mb-1">How's it going?</p>
      <p className="text-xs text-gray-400 mb-3">
        You're on set {setNum}. Want to keep the intensity or ease up for the rest?
      </p>
      <div className="flex gap-2">
        <button
          onClick={onKeepPushing}
          className="flex-1 py-2 rounded-lg bg-emerald-500/20 text-emerald-400 text-xs font-bold hover:bg-emerald-500/30 transition-colors"
          data-testid="keep-pushing-btn"
        >
          Keep Pushing
        </button>
        <button
          onClick={onSwitchDeload}
          className="flex-1 py-2 rounded-lg bg-amber-500/20 text-amber-400 text-xs font-bold hover:bg-amber-500/30 transition-colors"
          data-testid="switch-deload-btn"
        >
          Switch to Deload
        </button>
      </div>
    </motion.div>
  );
};

export { ReadinessCheckIn, DeloadSuggestion, MidWorkoutCheck };
